import { useRef } from "react";
import type { JSX, ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { ServiceStatusBanner } from "../features/billing/ServiceStatusBanner";
import { LegalDocumentsPrompt } from "../features/tenant/LegalDocuments";
import { useAuth } from "../shared/auth/AuthContext";
import { useI18n } from "../shared/i18n/I18nContext";
import { useMenuSignOut } from "../shared/session/signOutRequest";
import { useApplyBranding, useBranding } from "../shared/session/useBranding";
import { useMe } from "../shared/session/useMe";
import { useNavigationRoles } from "../shared/session/useNavigationRoles";
import { AccountMenu } from "../shared/ui/AccountMenu";
import { AnnouncementOutlet } from "../shared/ui/Announcer";
import { brandMarkPath } from "../shared/ui/BrandHeader";
import { cx } from "../shared/ui/classNames";
import { Icon } from "../shared/ui/Icon";
import { PageBannersProvider } from "../shared/ui/PageBanners";
import { UnsavedInputContext, useUnsavedInputTracking } from "../shared/ui/unsavedInput";
import { useMediaQuery } from "../shared/ui/useMediaQuery";
import layout from "./AppLayout.module.css";
import { isBelowMore, layoutNavigation, routePaths, wideLayoutQuery } from "./navigation";
import { useKeepFocusInView } from "./useKeepFocusInView";
import { useVisibleNavigation } from "./useVisibleNavigation";

/** Properties of {@link AppLayout}. */
export interface AppLayoutProps {
  /**
   * Compact connectivity status inside the sticky app bar, visible on every page and after
   * scrolling (UX-102).
   */
  readonly indicator?: ReactNode;
  /**
   * Compact clock (F-75): on wide screens in the app bar between the connectivity status and the
   * account, on phones as a bar right above the bottom navigation (F-77, ADR-0201). It decides
   * itself for whom and on which pages it is shown; the bar takes room only while it shows.
   */
  readonly clock?: ReactNode;
  /** Status area below the app bar (synchronization details, update hint). */
  readonly status?: ReactNode;
}

/**
 * Application shell: skip link, top app bar with the connectivity indicator, status area,
 * role-based navigation and the content area. The banners of the shell (service status, pending
 * legal document) follow the heading of each page instead of preceding it (UX-001); errors of a
 * screen replace only the content (UX-109, see the routes).
 *
 * On phones the navigation is a bottom navigation bar in the thumb zone with at most four
 * destinations plus "Mehr" (Material 3 navigation bar, HIG tab bar); the other areas, the
 * preferences and the sign-out are on the "Mehr" screen. Wide screens show every area in the
 * navigation drawer and no "Mehr" (F-74). Both use one navigation landmark; entries that do not
 * belong to the current layout are hidden with CSS and therefore also from assistive technology.
 * After an app start without connection the entries follow the last known roles of the same person
 * and tenant (F-05).
 *
 * The account menu at the name in the app bar (F-74) leads to „Einstellungen“ and signs out, in
 * every layout. The app bar holds, from the start: the home link, the connectivity status, on wide
 * screens the compact clock of F-75 (an item that does not shrink, ADR-0200), and at the end the
 * account menu, whose name gives way first. On phones the compact clock is a bar right above the
 * bottom navigation instead, like a mini player (F-77, ADR-0201); it follows the status area in
 * the document, so the focus reaches it right before the navigation it sits on.
 *
 * @param props - Component properties.
 * @returns The layout element.
 */
export function AppLayout(props: AppLayoutProps): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const me = useMe();
  const roles = useNavigationRoles();
  const visible = useVisibleNavigation(roles ?? []);
  const { bar, overflow } = layoutNavigation(visible);
  const barPaths = new Set(bar.map((item) => item.path));
  const pathname = useLocation().pathname;
  // "all": below „Mehr“ in every layout; "phone": only through the overflow of the bar (F-57).
  // Both mark „Mehr“ in the bottom bar of phones only, wide screens have no „Mehr“ (F-74).
  const belowMore = isBelowMore(pathname, [], visible)
    ? "all"
    : isBelowMore(pathname, overflow, visible)
      ? "phone"
      : undefined;
  const displayName = me.data?.displayName ?? user?.displayName ?? null;
  const branding = useBranding();
  useApplyBranding(branding.data, document.documentElement);
  const logo = branding.data?.logoDataUrl ?? null;
  const signOutFromMenu = useMenuSignOut(routePaths.settings);
  const mainRef = useRef<HTMLElement>(null);
  useKeepFocusInView(mainRef);
  // Input of the person on the current page, so the compact clock never drops it unasked (F-78).
  const unsavedInput = useUnsavedInputTracking(mainRef);
  // One clock per layout: a change of width moves it (and closes its panel).
  const wide = useMediaQuery(wideLayoutQuery);

  const shell = (
    <UnsavedInputContext value={unsavedInput}>
      <div className={layout["shell"]}>
        <a className={layout["skipLink"]} href="#content">
          {t("app.skipToContent")}
        </a>
        <header className={layout["header"]}>
          <Link className={layout["brand"]} to="/">
            {logo === null ? (
              <img className={layout["logo"]} src={brandMarkPath} alt="" width="28" height="28" />
            ) : (
              <img
                className={layout["tenantLogo"]}
                src={logo}
                alt={branding.data?.tenantName ?? ""}
                height="28"
              />
            )}
            <span className={layout["brandName"]}>{t("app.title")}</span>
          </Link>
          {props.indicator === undefined ? null : (
            <div className={layout["indicator"]}>{props.indicator}</div>
          )}
          {wide ? props.clock : null}
          {displayName === null ? null : (
            <AccountMenu
              className={layout["account"]}
              name={displayName}
              label={t("account.menu", { name: displayName })}
              caption={t("auth.signedInAs", { name: displayName })}
              items={[
                {
                  key: "settings",
                  label: t("account.settings"),
                  icon: "settings",
                  to: routePaths.settings,
                },
                {
                  key: "signOut",
                  label: t("auth.logout"),
                  icon: "signOut",
                  onSelect: signOutFromMenu,
                },
              ]}
            />
          )}
        </header>
        {props.status === undefined ? null : <div className={layout["status"]}>{props.status}</div>}
        {wide || props.clock === undefined ? null : (
          <div className={layout["clockBar"]}>{props.clock}</div>
        )}
        <nav className={layout["nav"]} aria-label={t("nav.main")}>
          <ul className={layout["navList"]}>
            {visible.map((item) => (
              <li
                key={item.path}
                className={barPaths.has(item.path) ? undefined : layout["overflowItem"]}
              >
                <NavLink className={cx(layout["navLink"])} to={item.path} end={item.path === "/"}>
                  <Icon name={item.icon} className={layout["navIcon"]} />
                  <span className={layout["navLabel"]}>{t(item.navLabelKey ?? item.labelKey)}</span>
                </NavLink>
              </li>
            ))}
            <li className={layout["phoneOnlyItem"]}>
              <NavLink
                className={cx(layout["navLink"])}
                to={routePaths.more}
                data-below-more={belowMore}
              >
                <Icon name="more" className={layout["navIcon"]} />
                <span className={layout["navLabel"]}>{t("nav.more")}</span>
              </NavLink>
            </li>
          </ul>
        </nav>
        <main ref={mainRef} id="content" className={layout["main"]} tabIndex={-1}>
          {/* Toasts at the top of the content area; they take no pointer events, so clicks go through (F-61). */}
          <AnnouncementOutlet />
          <PageBannersProvider
            banners={
              <>
                <ServiceStatusBanner />
                <LegalDocumentsPrompt />
              </>
            }
          >
            <Outlet />
          </PageBannersProvider>
        </main>
      </div>
    </UnsavedInputContext>
  );

  return shell;
}
